/**
 * 영역 7.2 — 로그인 (소셜 전용).
 *
 * Figma SoT: TEMPLATE-Design-System 1503:22142 (SNS 아이콘 화면)
 *
 * 흐름: 소셜 버튼 → provider 인증 → **기존 회원이면 그대로 로그인**하고 원래 화면으로 복귀한다.
 * 처음 보는 계정일 때만 서버가 `NEED_AGE` + 가입 대기표를 돌려주고, 그때 연령을 확인해
 * 가입을 마무리한다(OI-43 · 2026-09-15). 로그인하는 사람은 생년월일을 보지 않는다.
 *
 * 2026-08-04 재설계로 본인인증·이메일 로그인·카카오·네이버에 이어 약관 동의·닉네임 입력 화면까지
 * 없앴다. 약관은 아래 안내 문구와 링크로 갈음하고, 닉네임은 provider 이름(없으면 서버 자동 생성)을 쓴다.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useActivityStore } from "../../store/activityStore";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import {
  consumePendingAuthIntent,
  discardAuthReturnPath,
} from "../../navigation/pendingAuthIntent";
import { executeNavigateIntent } from "../../navigation/useRequireAuth";
import { Button } from "@astryxdesign/core/Button";
import AppBar from "../../components/layout/AppBar";
import { useBackDispatcher } from "../../navigation/dispatchBack";
import DebugUnlockDialog from "../../components/feedback/DebugUnlockDialog";
import AgeGateSheet from "../../components/auth/AgeGateSheet";
import { useDebugStore } from "../../store/debugStore";
import { tokenResponseToAuthUser, type TokenResponse } from "../../api/auth";
import {
  assertTokens,
  completeSocialSignup,
  startGoogleAuth,
  startAppleAuth,
  type SocialAuthResult,
} from "../../api/social";
import { startGoogleFlow, startAppleFlow } from "../../lib/socialAuth";
import { nativeGoogleLogin } from "../../native/socialAuth/google";
import { nativeAppleLogin, startNativeAppleWebFlow } from "../../native/socialAuth/apple";
import { useAuthStore, type AuthUser } from "../../store/authStore";
import { apiErrorMessage, isUserCancelError } from "../../lib/errors";
import { track, identifyUser } from "../../lib/tracking";
import { triggerHaptic } from "../../native/haptics";
import { ensurePushPermission } from "../../native/pushPermission";
import { isNative } from "../../native";
import googleIconSvg from "../../assets/icons/brand/google.svg?raw";
import appleIconSvg from "../../assets/icons/brand/apple.svg?raw";
import peekaSymbol from "../../assets/brand/peeka-symbol-orange.png";
import styles from "./AuthPage.module.css";

/**
 * 디버그 메뉴 진입 (개발자/QA 전용) — 내 정보와 동일 패턴.
 * 로고를 TAP_WINDOW_MS (3초) 안에 TAP_THRESHOLD (10) 번 탭 → DebugUnlockDialog.
 */
const TAP_THRESHOLD = 10;
const TAP_WINDOW_MS = 3000;

type SocialProvider = "google" | "apple";

interface SnsButtonProps {
  provider: SocialProvider;
  label: string;
  iconSvg: string;
  loading: boolean;
  disabled: boolean;
  onPressed: () => void;
}

/**
 * SNS 로그인 — **전폭 버튼 + "…로 계속하기"** (design 8dc51af).
 * 원형 아이콘 아래 계정 이름만 있으면 브랜드 마크가 **무엇이 되는지**를 말하지
 * 않는다. 동작까지 한 줄에 적으면 처음 보는 사람도 고민 없이 고른다.
 */
function SnsButton({ provider, label, iconSvg, loading, disabled, onPressed }: SnsButtonProps) {
  // SnsButton 은 LoginPage 밖의 하위 컴포넌트라 자체적으로 useTranslation 을 잡는다.
  const { t } = useTranslation("common");
  const text = t("auth.snsContinue", { provider: label });
  return (
    <button
      type="button"
      className={styles.snsButton}
      data-provider={provider}
      disabled={disabled}
      onClick={disabled ? undefined : onPressed}
      aria-label={`${text}${loading ? ` ${t("auth.inProgress")}` : ""}`}
    >
      <span
        className={styles.snsButtonIcon}
        dangerouslySetInnerHTML={{ __html: iconSvg }}
      />
      {text}
    </button>
  );
}

export default function LoginPage() {
  const { t } = useTranslation("common");
  const { t: tMe } = useTranslation("me");
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const activityNavigator = useActivityNavigator();

  const dispatchBack = useBackDispatcher();
  const [loading, setLoading] = useState<SocialProvider | null>(null);
  const [serverError, setServerError] = useState<string | undefined>();

  // 디버그 메뉴 unlock 상태 + 로고 10-tap 카운터
  const debugUnlocked = useDebugStore((s) => s.unlocked);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const tapCountRef = useRef(0);
  const firstTapAtRef = useRef(0);

  const handleLogoTap = () => {
    if (debugUnlocked) return;
    const now = Date.now();
    if (
      tapCountRef.current === 0 ||
      now - firstTapAtRef.current > TAP_WINDOW_MS
    ) {
      tapCountRef.current = 1;
      firstTapAtRef.current = now;
      return;
    }
    tapCountRef.current += 1;
    if (tapCountRef.current >= TAP_THRESHOLD) {
      tapCountRef.current = 0;
      firstTapAtRef.current = 0;
      setDebugDialogOpen(true);
    }
  };

  const redirectAfterLogin = (location.state as { from?: string } | null)?.from ?? "/";

  /** 약관/개인정보 뷰어를 오버레이로 연다 — 로그인 화면은 그대로 아래에 남는다. */
  const openTerms = useCallback(
    (type: "service" | "privacy") => {
      activityNavigator.open({ url: `/terms/view?type=${type}` });
    },
    [activityNavigator],
  );

  /**
   * OAuth 실패 1건. ⚠ **서버 문구(apiErrorMessage)를 그대로 보내지 않는다** — 자유 텍스트라
   * PII 가 섞일 수 있다(GA 약관). 거친 라벨로만 남긴다:
   *   token_missing      : provider 는 성공했는데 idToken 이 안 왔다
   *   auth_failed        : 우리 서버 교환 단계 실패 (콘솔 미등록·redirect URI 불일치가 여기)
   *   flow_start_failed  : 웹 리다이렉트 자체를 시작 못 함
   * 사용자 취소는 **세지 않는다** — 호출부가 isUserCancelError 로 먼저 빠져나간다.
   */
  const trackOAuthError = (
    provider: SocialProvider,
    reason: "token_missing" | "auth_failed" | "flow_start_failed",
  ) => {
    track("auth_oauth_error", { provider, reason });
  };

  const applyTokens = async (
    tokens: TokenResponse,
    provider: AuthUser["provider"],
  ) => {
    const user = tokenResponseToAuthUser(tokens, provider);
    setAuth({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user,
    });
    identifyUser(user.memberSerialNo);
    // provider 별 이벤트(auth_oauth_google/apple)를 쓰지 않는다 — GA4 이벤트 수는
    // 속성 500개 상한이 있고, provider 는 파라미터로 두면 리포트에서 쪼갤 수 있다.
    track("auth_login_success", {
      memberSerialNo: user.memberSerialNo,
      provider: provider ?? "",
    });
    await triggerHaptic("light");

    // 로그인 성공 시 알림 권한 1회 요청 (fire-and-forget). setupAfterLogin 은 lifecycle hook 이
    // 이미 처리하며, canAsk 는 첫 OS 다이얼로그 후 false 가 되므로 이중 프롬프트 없음.
    void ensurePushPermission({ allowSettingsPrompt: false });

    // Phase 3: if mounted as an AuthActivity entry, close the activity and
    // consume the pending intent (D5). Otherwise fall back to legacy route
    // behavior (navigate to state.from), preserving deep-link/refresh paths.
    const authActivity = useActivityStore
      .getState()
      .activities.find((a) => a.kind === "auth");
    if (authActivity) {
      activityNavigator.close(authActivity.id);
      const intent = consumePendingAuthIntent();
      if (intent && intent.type === "navigate") {
        executeNavigateIntent(intent, activityNavigator);
      }
      discardAuthReturnPath();
      return;
    }

    discardAuthReturnPath();
    navigate(redirectAfterLogin, { replace: true });
  };

  /**
   * 연령 게이트 (OI-43) — **처음 보는 계정일 때만** 연다.
   *
   * 서버가 `NEED_AGE` 를 주면 아직 회원이 만들어지지 않은 상태다. 생년월일을 받아
   * 대기표와 함께 되보내야 가입이 끝난다. 기존 회원 로그인은 이 분기를 지나지 않는다.
   */
  const [signupTicket, setSignupTicket] = useState<string | null>(null);
  /** 대기표를 내준 provider — 가입 성공 이벤트와 토큰 적용에 쓴다. */
  const signupProviderRef = useRef<SocialProvider | null>(null);

  /**
   * 소셜 인증 결과 처리 — 기존 회원이면 토큰이 와서 곧바로 로그인 완료,
   * 처음 보는 계정이면 대기표만 와서 연령 확인으로 넘어간다.
   */
  const handleSocialResult = (result: SocialAuthResult, provider: SocialProvider) => {
    if (result.kind === "NEED_AGE") {
      signupProviderRef.current = provider;
      setSignupTicket(result.signupTicket);
      return;
    }
    if (result.newMember) {
      track("auth_signup_success", { provider });
    }
    void applyTokens(assertTokens(result), provider);
  };

  /** 연령 확인 통과 → 대기표로 가입 마무리. */
  const handleAgeGatePassed = async (birthYmd: string) => {
    const ticket = signupTicket;
    const provider = signupProviderRef.current;
    if (!ticket || !provider) return;
    setSignupTicket(null);
    setLoading(provider);
    try {
      const result = await completeSocialSignup({ signupTicket: ticket, birthYmd });
      // 대기표를 들고 있는 사이 같은 계정이 다른 경로로 가입됐으면 서버가 로그인으로 처리한다 —
      // 그때는 가입 이벤트가 아니다.
      if (result.newMember) {
        track("auth_signup_success", { provider });
      }
      await applyTokens(assertTokens(result), provider);
    } catch (err) {
      // 대기표는 1회용이라 여기서 실패하면 소셜 로그인부터 다시다(서버 문구가 그렇게 안내한다).
      trackOAuthError(provider, "auth_failed");
      setServerError(apiErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  /**
   * Google 로그인 — 네이티브/웹 분기.
   */
  const handleGoogle = async () => {
    setServerError(undefined);
    track("auth_oauth_start", { provider: "google" });
    if (isNative()) {
      setLoading("google");
      try {
        const idToken = await nativeGoogleLogin();
        if (!idToken) {
          trackOAuthError("google", "token_missing");
          setServerError(t("auth.googleTokenError"));
          return;
        }
        const result = await startGoogleAuth({ idToken });
        handleSocialResult(result, "google");
      } catch (err) {
        if (isUserCancelError(err)) return;
        trackOAuthError("google", "auth_failed");
        setServerError(apiErrorMessage(err));
      } finally {
        setLoading(null);
      }
    } else {
      try {
        startGoogleFlow();
      } catch (err) {
        trackOAuthError("google", "flow_start_failed");
        setServerError(apiErrorMessage(err));
      }
    }
  };

  const handleApple = async () => {
    setServerError(undefined);
    track("auth_oauth_start", { provider: "apple" });
    // iOS 네이티브는 ASAuthorizationController. Android(네이티브지만 Apple SDK 없음)/웹은 web OAuth redirect.
    if (isNative()) {
      setLoading("apple");
      try {
        const native = await nativeAppleLogin();
        if (native) {
          const result = await startAppleAuth({ idToken: native.idToken, name: native.name });
          handleSocialResult(result, "apple");
          return;
        }
        // iOS 가 아닌 네이티브(Android) → Apple 은 SDK 부재 + 임베디드 WebView 차단 →
        // 시스템 Custom Tab 으로 웹 OAuth. 완료 시 BE 가 peeka:// 딥링크로 앱 복귀시킨다.
        await startNativeAppleWebFlow();
      } catch (err) {
        if (isUserCancelError(err)) return;
        trackOAuthError("apple", "auth_failed");
        setServerError(apiErrorMessage(err));
      } finally {
        setLoading(null);
      }
    } else {
      try {
        startAppleFlow();
      } catch (err) {
        trackOAuthError("apple", "flow_start_failed");
        setServerError(apiErrorMessage(err));
      }
    }
  };

  useEffect(() => {
    track("auth_login_view");
  }, []);

  const anyLoading = loading !== null;

  return (
    <div className={styles.screen} style={{ minHeight: "100%" }}>
      {/* Figma 1506:56097 — popup variant: 좌측 "로그인하기" 타이틀 + 우측 X close.
       * X 클릭은 auth activity 를 닫고 호출자에게 복귀 (dispatchBack 으로
       * SubActivity 동일한 back 메커니즘 사용). */}
      <AppBar
        variant="popup"
        title={t("auth.title")}
        onClosePressed={() => dispatchBack("in-app")}
      />

      <div className={styles.formSection}>
        <div className={styles.descriptionSection}>
          <h1 className={styles.descriptionTitle}>{t("auth.descriptionTitle")}</h1>
          <p className={styles.descriptionSubtitle}>{t("auth.descriptionSubtitle")}</p>
        </div>

        {/* ⚠ 문구와 버튼 사이만 한 단 더 벌린다 (design 736a0df). 부모(.formSection)의
            gap 을 키우면 오류 문구·디버그 버튼 사이까지 같이 벌어진다 — 벌리고 싶은
            것은 **읽는 것과 누르는 것 사이** 한 곳뿐이다. */}
        <div className={styles.initialChoiceList}>
          <div className={styles.snsList}>
            <SnsButton
              provider="google"
              label="Google"
              iconSvg={googleIconSvg}
              loading={loading === "google"}
              disabled={anyLoading}
              onPressed={() => void handleGoogle()}
            />
            <SnsButton
              provider="apple"
              label="Apple"
              iconSvg={appleIconSvg}
              loading={loading === "apple"}
              disabled={anyLoading}
              onPressed={() => void handleApple()}
            />
          </div>
        </div>

        {serverError && <div className={styles.serverError}>{serverError}</div>}

        {debugUnlocked && (
          <Button
            variant="secondary"
            size="lg"
            label={t("auth.debugMenu")}
            clickAction={() => navigate("/dev/debug-menu")}
            width="100%"
          />
        )}
      </div>

      {/* 심볼은 **화면 맨 아래, 가운데**다 (design 736a0df · 2026-08-28에 맨 위에서 내렸다).
          위에 있을 때는 화면에 들어서자마자 로고가 먼저 눈에 들어왔는데, 여기 온 사람이
          알아야 할 것은 **무엇을 할 수 있고 어떻게 들어가는가**(문구 + 버튼 둘)이지
          브랜드 이름이 아니다. 할 일을 위로 올리고 표식은 아래에서 화면을 닫는다.
          ⚠ 글자 워드마크에서 **심볼**(오렌지 번개)로 갈았다 — 글자로 쓴 'PEEKA' 가
            아래 두 버튼의 글자와 같은 무게로 읽혔다. 심볼은 표식이라 다른 층에 선다.
          ⚠ 로고 10-tap 으로 디버그 메뉴가 열린다(handleLogoTap). 자리를 옮겨도 그대로다. */}
      <div
        className={styles.logoSection}
        onClick={handleLogoTap}
        style={{ cursor: "default" }}
      >
        {/* ⚠ 장식이다 — 서비스 이름은 위 문장이 말한다. 원색판(symbol-orange)인 이유는
            이 화면이 늘 어두운 바탕이고 번개가 여기서 유일한 색이기 때문이다. */}
        <img
          className={styles.brandSymbol}
          src={peekaSymbol}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
      </div>

      {/* 약관 링크 — monprice 와 동일 배치(화면 하단 링크 2개).
          "…개인정보처리방침 및 서비스 약관 내용을 확인하세요" 문구는 앱이 그리는 게 아니라
          Google 로그인 바텀시트가 자동으로 넣어준다(OAuth 동의 화면에 등록한 URL 기준). */}
      {/* ⚠ 라벨은 me 네임스페이스의 legal 블록을 그대로 쓴다 (설정 > 약관과 같은 문서를
          가리키므로 이름이 갈리면 안 된다). 그래서 여기만 tMe 를 따로 잡는다. */}
      <nav className={styles.legalLinks} aria-label={tMe("legal.terms")}>
        <button type="button" className={styles.legalLink} onClick={() => openTerms("privacy")}>
          {tMe("legal.privacy")}
        </button>
        <button type="button" className={styles.legalLink} onClick={() => openTerms("service")}>
          {tMe("legal.terms")}
        </button>
      </nav>

      {/* 가입 대기표를 들고 있는 동안에만 뜬다 — 닫으면 대기표를 버리고(서버 쪽은 TTL 로 만료)
          회원은 만들어지지 않는다. */}
      <AgeGateSheet
        open={signupTicket !== null}
        onClose={() => setSignupTicket(null)}
        onPassed={(birthYmd) => void handleAgeGatePassed(birthYmd)}
      />

      <DebugUnlockDialog
        open={debugDialogOpen}
        onClose={() => setDebugDialogOpen(false)}
        onUnlock={() => navigate("/dev/debug-menu")}
      />
    </div>
  );
}
